import React, { useState, useEffect } from 'react';
import { ArtisanProduct, ArtisanProductVariation } from '@/services/api/artisan-product';
import { PlusCircleIcon, TrashIcon } from './icons/HeroIcons';
import { ImageUpload } from './ImageUpload';
import { uploadMenuImage } from '@/services/api/menu-image-upload';

interface ArtisanProductFormProps {
  product: ArtisanProduct | null; // null = 新規作成
  onSave: (data: Partial<ArtisanProduct>) => Promise<void>;
  onCancel: () => void;
}

const emptyVariation: ArtisanProductVariation = {
  name: '',
  price: 0,
  installation_price: 0,
  wholesale_price: 0,
  wholesale_installation_price: 0,
  sample_image_url: '',
};

const ArtisanProductForm: React.FC<ArtisanProductFormProps> = ({ product, onSave, onCancel }) => {
  const [productName, setProductName] = useState('');
  const [compatibleMaker, setCompatibleMaker] = useState('');
  const [compatibleCar, setCompatibleCar] = useState('');
  const [compatibleModel, setCompatibleModel] = useState('');
  const [compatibleNotes, setCompatibleNotes] = useState('');
  const [description, setDescription] = useState('');
  const [productPrice, setProductPrice] = useState(0);
  const [installationDescription, setInstallationDescription] = useState('');
  const [installationPrice, setInstallationPrice] = useState(0);
  const [installationEstimatedTime, setInstallationEstimatedTime] = useState('');
  const [wholesalePrice, setWholesalePrice] = useState(0);
  const [wholesaleInstallationPrice, setWholesaleInstallationPrice] = useState(0);
  const [sampleImageUrl, setSampleImageUrl] = useState('');
  const [sampleImageUrl2, setSampleImageUrl2] = useState('');
  const [variations, setVariations] = useState<ArtisanProductVariation[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage1, setIsUploadingImage1] = useState(false);
  const [isUploadingImage2, setIsUploadingImage2] = useState(false);
  const [uploadingVariationIndex, setUploadingVariationIndex] = useState<number | null>(null);

  useEffect(() => {
    if (product) {
      setProductName(product.product_name);
      setCompatibleMaker(product.compatible_maker);
      setCompatibleCar(product.compatible_car);
      setCompatibleModel(product.compatible_model);
      setCompatibleNotes(product.compatible_notes);
      setDescription(product.description);
      setProductPrice(product.product_price);
      setInstallationDescription(product.installation_description);
      setInstallationPrice(product.installation_price);
      setInstallationEstimatedTime(product.installation_estimated_time);
      setWholesalePrice(product.wholesale_price);
      setWholesaleInstallationPrice(product.wholesale_installation_price);
      setSampleImageUrl(product.sample_image_url);
      setSampleImageUrl2(product.sample_image_url_2);
      setVariations(product.variations || []);
      setIsPublic(product.is_public);
    }
  }, [product]);

  const handleImageUpload = async (file: File | null, setter: (url: string) => void, setUploading: (v: boolean) => void) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadMenuImage(file, 'artisan' as any);
      setter(result.public_url);
    } catch (error) {
      console.error('画像アップロードエラー:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleVariationImageUpload = async (file: File | null, index: number) => {
    if (!file) return;
    setUploadingVariationIndex(index);
    try {
      const result = await uploadMenuImage(file, 'artisan' as any);
      updateVariation(index, 'sample_image_url', result.public_url);
    } catch (error) {
      console.error('バリエーション画像アップロードエラー:', error);
    } finally {
      setUploadingVariationIndex(null);
    }
  };

  const handleSubmit = async () => {
    if (!productName.trim()) return;
    setIsSaving(true);
    try {
      await onSave({
        product_name: productName,
        compatible_maker: compatibleMaker,
        compatible_car: compatibleCar,
        compatible_model: compatibleModel,
        compatible_notes: compatibleNotes,
        description,
        product_price: productPrice,
        installation_description: installationDescription,
        installation_price: installationPrice,
        installation_estimated_time: installationEstimatedTime,
        wholesale_price: wholesalePrice,
        wholesale_installation_price: wholesaleInstallationPrice,
        sample_image_url: sampleImageUrl,
        sample_image_url_2: sampleImageUrl2,
        variations,
        is_public: isPublic,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addVariation = () => {
    setVariations([...variations, { ...emptyVariation }]);
  };

  const updateVariation = (index: number, field: keyof ArtisanProductVariation, value: string | number) => {
    const updated = [...variations];
    updated[index] = { ...updated[index], [field]: value };
    setVariations(updated);
  };

  const removeVariation = (index: number) => {
    setVariations(variations.filter((_, i) => i !== index));
  };

  const moveVariation = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= variations.length) return;
    const updated = [...variations];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setVariations(updated);
  };

  const inputClass = "w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 focus:outline-none focus:border-indigo-500 text-sm";
  const labelClass = "block text-sm font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 z-10">
          <h3 className="text-lg font-semibold text-indigo-300">
            {product ? 'サービス品を編集' : '新規サービス品を作成'}
          </h3>
        </div>

        <div className="px-6 py-4 space-y-6">
          {/* 商品基本情報 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-3 border-b border-gray-700 pb-2">商品基本情報</h4>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>商品名 *</label>
                <input type="text" value={productName} onChange={(e) => setProductName(e.target.value)} className={inputClass} placeholder="例: フロントバンパーラッピング" />
              </div>
              <div>
                <label className={labelClass}>商品説明</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={`${inputClass} h-20 resize-none`} placeholder="商品の説明" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>商品価格 (円)</label>
                  <input type="number" value={productPrice} onChange={(e) => setProductPrice(Number(e.target.value))} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>卸価格 (円)</label>
                  <input type="number" value={wholesalePrice} onChange={(e) => setWholesalePrice(Number(e.target.value))} className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>商品画像サンプル1</label>
                  {sampleImageUrl && (
                    <div className="mb-2 relative">
                      <img src={sampleImageUrl} alt="サンプル1" className="w-full h-32 object-cover rounded-lg border border-gray-600" />
                      <button
                        onClick={() => setSampleImageUrl('')}
                        className="absolute top-1 right-1 bg-gray-900 bg-opacity-75 text-red-400 hover:text-red-300 rounded-full p-1"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {isUploadingImage1 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-400"></div>
                      アップロード中...
                    </div>
                  ) : (
                    <ImageUpload
                      onImageSelect={(file) => handleImageUpload(file, setSampleImageUrl, setIsUploadingImage1)}
                      label="画像を選択"
                      initialPreviewUrl=""
                    />
                  )}
                </div>
                <div>
                  <label className={labelClass}>商品画像サンプル2</label>
                  {sampleImageUrl2 && (
                    <div className="mb-2 relative">
                      <img src={sampleImageUrl2} alt="サンプル2" className="w-full h-32 object-cover rounded-lg border border-gray-600" />
                      <button
                        onClick={() => setSampleImageUrl2('')}
                        className="absolute top-1 right-1 bg-gray-900 bg-opacity-75 text-red-400 hover:text-red-300 rounded-full p-1"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {isUploadingImage2 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-400"></div>
                      アップロード中...
                    </div>
                  ) : (
                    <ImageUpload
                      onImageSelect={(file) => handleImageUpload(file, setSampleImageUrl2, setIsUploadingImage2)}
                      label="画像を選択"
                      initialPreviewUrl=""
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 適合情報 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-3 border-b border-gray-700 pb-2">適合情報</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>適合メーカー</label>
                <input type="text" value={compatibleMaker} onChange={(e) => setCompatibleMaker(e.target.value)} className={inputClass} placeholder="例: トヨタ" />
              </div>
              <div>
                <label className={labelClass}>適合車種</label>
                <input type="text" value={compatibleCar} onChange={(e) => setCompatibleCar(e.target.value)} className={inputClass} placeholder="例: スープラ" />
              </div>
              <div>
                <label className={labelClass}>適合モデル</label>
                <input type="text" value={compatibleModel} onChange={(e) => setCompatibleModel(e.target.value)} className={inputClass} placeholder="例: A90" />
              </div>
            </div>
            <div className="mt-3">
              <label className={labelClass}>適合備考</label>
              <textarea value={compatibleNotes} onChange={(e) => setCompatibleNotes(e.target.value)} className={`${inputClass} h-16 resize-none`} placeholder="その他の適合情報" />
            </div>
          </div>

          {/* 施工情報 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-3 border-b border-gray-700 pb-2">施工情報</h4>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>施工説明</label>
                <textarea value={installationDescription} onChange={(e) => setInstallationDescription(e.target.value)} className={`${inputClass} h-20 resize-none`} placeholder="施工方法の説明" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>施工価格 (円)</label>
                  <input type="number" value={installationPrice} onChange={(e) => setInstallationPrice(Number(e.target.value))} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>施工卸価格 (円)</label>
                  <input type="number" value={wholesaleInstallationPrice} onChange={(e) => setWholesaleInstallationPrice(Number(e.target.value))} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>施工予想時間</label>
                  <input type="text" value={installationEstimatedTime} onChange={(e) => setInstallationEstimatedTime(e.target.value)} className={inputClass} placeholder="例: 2時間" />
                </div>
              </div>
            </div>
          </div>

          {/* バリエーション */}
          <div>
            <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2">
              <h4 className="text-sm font-semibold text-gray-300">バリエーション</h4>
              <button onClick={addVariation} className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300">
                <PlusCircleIcon className="w-4 h-4" />
                追加
              </button>
            </div>
            {variations.length === 0 ? (
              <p className="text-sm text-gray-500">バリエーションはありません</p>
            ) : (
              <div className="space-y-4">
                {variations.map((v, i) => (
                  <div key={i} className="bg-gray-750 border border-gray-600 rounded-lg p-3 relative" style={{ backgroundColor: 'rgb(42, 45, 55)' }}>
                    <div className="absolute top-2 right-2 flex items-center gap-1">
                      <button onClick={() => moveVariation(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-indigo-400 disabled:opacity-30 disabled:hover:text-gray-500" title="上へ">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                      </button>
                      <button onClick={() => moveVariation(i, 'down')} disabled={i === variations.length - 1} className="text-gray-500 hover:text-indigo-400 disabled:opacity-30 disabled:hover:text-gray-500" title="下へ">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      <button onClick={() => removeVariation(i)} className="text-gray-500 hover:text-red-400" title="削除">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className={labelClass}>バリエーション名</label>
                        <input type="text" value={v.name} onChange={(e) => updateVariation(i, 'name', e.target.value)} className={inputClass} placeholder="例: Sサイズ" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className={labelClass}>＋価格 (円)</label>
                          <input type="number" value={v.price} onChange={(e) => updateVariation(i, 'price', Number(e.target.value))} className={inputClass} />
                        </div>
                        <div>
                          <label className={labelClass}>＋施工価格 (円)</label>
                          <input type="number" value={v.installation_price} onChange={(e) => updateVariation(i, 'installation_price', Number(e.target.value))} className={inputClass} />
                        </div>
                        <div>
                          <label className={labelClass}>＋卸価格 (円)</label>
                          <input type="number" value={v.wholesale_price} onChange={(e) => updateVariation(i, 'wholesale_price', Number(e.target.value))} className={inputClass} />
                        </div>
                        <div>
                          <label className={labelClass}>＋卸施工価格 (円)</label>
                          <input type="number" value={v.wholesale_installation_price} onChange={(e) => updateVariation(i, 'wholesale_installation_price', Number(e.target.value))} className={inputClass} />
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>バリエーション画像</label>
                        {v.sample_image_url && (
                          <div className="mb-2 relative">
                            <img src={v.sample_image_url} alt={v.name} className="w-full h-24 object-cover rounded-lg border border-gray-600" />
                            <button
                              onClick={() => updateVariation(i, 'sample_image_url', '')}
                              className="absolute top-1 right-1 bg-gray-900 bg-opacity-75 text-red-400 hover:text-red-300 rounded-full p-1"
                            >
                              <TrashIcon className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {uploadingVariationIndex === i ? (
                          <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-400"></div>
                            アップロード中...
                          </div>
                        ) : (
                          <ImageUpload
                            onImageSelect={(file) => handleVariationImageUpload(file, i)}
                            label="画像を選択"
                            initialPreviewUrl=""
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 公開設定 */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500" />
              <span className="text-sm text-gray-300">公開する</span>
            </label>
          </div>
        </div>

        {/* フッターボタン */}
        <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 px-6 py-4 flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-gray-400 hover:text-gray-200 text-sm">
            キャンセル
          </button>
          <button
            onClick={handleSubmit}
            disabled={!productName.trim() || isSaving || isUploadingImage1 || isUploadingImage2 || uploadingVariationIndex !== null}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {isSaving ? '保存中...' : product ? '更新' : '作成'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtisanProductForm;
